import DateAdapter from "@date-io/luxon";
import { useLocalize } from "@faro-lotv/foreign-observers";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

/** The format options for the date */
export type FormatDate = (
  date: string | number | Date,
  formatOptions?: Intl.DateTimeFormatOptions
) => string;

interface UseDateTime {
  /** The function to format and translate dates */
  formatDate: FormatDate;
}

interface FormatAndTranslateDate {
  /** The date to format and translate */
  date: string | number | Date;

  /** The language code to use for the translation */
  languageCode: string;

  /** The format options */
  formatOptions?: Intl.DateTimeFormatOptions;
}

/**
 * Hook to format and translate dates
 *
 * @returns The formatDate function that can be used to format and translate dates
 */
export function useDateTime(): UseDateTime {
  const { currentLanguage } = useLocalize();

  const luxonAdapter = useMemo(
    () => new DateAdapter({ locale: currentLanguage.code }),
    [currentLanguage.code]
  );

  const formatAndTranslateDate = useCallback(
    ({
      date,
      languageCode,
      formatOptions = DateTime.DATE_FULL,
    }: FormatAndTranslateDate): string => {
      const luxonDate = luxonAdapter.date(new Date(date));
      const unixMsec = luxonDate?.toMillis() ?? 0;

      // We cannot easily compare using luxonDate.{ year, month, day }, since they're already in the user's timezone.
      // So we check instead if the Unix timestamp is 0.
      // If we find any almost-zero dates due to timezone-related bugs, the check could be relaxed to:
      // Math.abs(unixMsec) < ONE_DAY_IN_MSEC
      return luxonDate && unixMsec !== 0
        ? luxonDate.setLocale(languageCode).toLocaleString(formatOptions)
        : "";
    },
    [luxonAdapter]
  );

  const formatDate: UseDateTime["formatDate"] = useCallback(
    (date, formatOptions) =>
      formatAndTranslateDate({
        date,
        languageCode: currentLanguage.code,
        formatOptions,
      }),
    [formatAndTranslateDate, currentLanguage.code]
  );

  return { formatDate };
}
